'use client'

import { cn } from '@/lib/utils'

import {
  EASE,
  HAIRLINE,
  SectionHead,
  restoreDashes,
  useReveal,
} from '@/components/page-kit'
import { HRMS } from './data'

/* Each feature already carries a `stat` in the source data — "Single source of
   truth", "3 days → 4 hours", "Zero manual entry", "45% productivity uplift".
   The shared template buries those as 10px uppercase labels inside four
   identical cards. Here they are the anchor of the row, which means the section
   is carried by content that already exists rather than by decoration.

   Heading is a verbatim fragment of `description`. */

export function HrmsFeatures() {
  const list = useReveal<HTMLDivElement>(0.1)

  return (
    <section className="pt-16 sm:pt-20 md:pt-28">
      <div className="container mx-auto">
        <SectionHead
          eyebrow="Key features"
          title="HRMS transforms manual HR tasks into automated workflows."
        />

        <div ref={list.ref} className={cn('mt-10 border-t sm:mt-12', HAIRLINE)}>
          {HRMS.features.map((f, i) => (
            <article
              key={f.title}
              className={cn(
                'grid grid-cols-1 gap-x-10 gap-y-3 border-b py-7 sm:py-9',
                'lg:grid-cols-[minmax(0,300px)_1fr]',
                HAIRLINE
              )}
              style={{
                opacity: list.shown ? 1 : 0,
                transform: list.shown ? 'none' : 'translateY(12px)',
                transition: `opacity 520ms ${EASE} ${i * 90}ms, transform 520ms ${EASE} ${i * 90}ms`,
              }}
            >
              <div className="flex items-baseline gap-3 lg:block">
                <span className="text-[11px] font-semibold tabular-nums text-muted-foreground/60 lg:hidden">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <p className="font-heading text-xl font-normal leading-tight text-emerald-600 sm:text-2xl lg:text-[26px] dark:text-primary-active">
                  {f.stat}
                </p>
              </div>

              <div className="min-w-0">
                <h3 className="font-heading text-lg font-normal leading-snug text-foreground sm:text-xl">
                  {f.title}
                </h3>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
                  {restoreDashes(f.description)}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}
